import { useState, useRef, useEffect } from 'react'
import { searchNames } from '../utils/nameIndex'
import type { NameIndexEntry } from '../types'

interface Props {
  onSelectRegion: (regionId: string) => void
  onSelectFigure: (figureId: string) => void
}

export default function SearchBar({ onSelectRegion, onSelectFigure }: Props) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [hover, setHover] = useState(0)
  const ref = useRef<HTMLDivElement>(null)
  const results: NameIndexEntry[] = q ? searchNames(q, 8) : []

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  function pick(idx: number) {
    const r = results[idx]
    if (!r) return
    // Dispatch to the right axis. A search entry has either a regionId or a
    // figureId (mutually exclusive by construction in nameIndex.ts).
    if (r.kind === 'figure' && r.figureId) {
      onSelectFigure(r.figureId)
    } else if (r.regionId) {
      onSelectRegion(r.regionId)
    }
    setQ(r.display)
    setOpen(false)
  }

  return (
    <div ref={ref} className="relative">
      <div className="flex items-center gap-2 px-3 py-2 bg-parchment-200/70 border border-ink-300/10 rounded-md focus-within:border-saffron-300 focus-within:bg-parchment-50 transition-colors">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-ink-50">
          <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>
        </svg>
        <input
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); setHover(0) }}
          onFocus={() => setOpen(true)}
          onKeyDown={e => {
            if (!open) return
            if (e.key === 'ArrowDown') { setHover(h => Math.min(h + 1, results.length - 1)); e.preventDefault() }
            else if (e.key === 'ArrowUp') { setHover(h => Math.max(h - 1, 0)); e.preventDefault() }
            else if (e.key === 'Enter') { pick(hover); e.preventDefault() }
            else if (e.key === 'Escape') { setOpen(false) }
          }}
          placeholder="Pataliputra, Hampi, Krishna, Yājñavalkya, Ashoka..."
          className="bg-transparent outline-none w-full text-sm text-ink-300 placeholder:text-ink-50/60"
          aria-label="Search place names"
        />
      </div>
      {open && results.length > 0 && (
        <ul role="listbox" className="absolute left-0 right-0 mt-1 bg-parchment-50 border border-ink-300/15 rounded-md shadow-card overflow-hidden z-20">
          {results.map((r, i) => (
            <li
              key={`${r.regionId}-${r.q}-${i}`}
              role="option"
              aria-selected={i === hover}
              onMouseEnter={() => setHover(i)}
              onMouseDown={() => pick(i)}
              className={`px-3 py-2 cursor-pointer flex justify-between items-baseline text-sm iast ${i === hover ? 'bg-saffron-50' : ''}`}
            >
              <span className="text-ink-300">{r.display}</span>
              <span className="text-[10px] uppercase tracking-wider text-ink-50">{labelFor(r.kind)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function labelFor(kind: NameIndexEntry['kind']) {
  switch (kind) {
    case 'historical-region': return 'historical region'
    case 'historical-city':   return 'historical city'
    case 'modern-region':     return 'modern region'
    case 'modern-city':       return 'modern city'
    case 'figure':            return 'person'
    default:                  return kind
  }
}
